(function() {
	"use strict";

	// Tracks the upgrade selections (power meter / wheelset / handle bar /
	// group set / bottom bracket) for whichever bike was most recently
	// submitted, so they can be added as extra line items once Minimog's ajax
	// cart confirms the bike itself was added.
	window.selectedBikeUpgrades = {
		productId: null,
		bundleId: null,
		variants: []
	};

	function formatMoney(cents) {
		return "$" + (cents / 100).toFixed(2);
	}

	function getRoot(el) {
		return el.closest(".bike-customization-block");
	}

	function getSelectedRadioValue(scope, name, fallback) {
		const checked = scope.querySelector(`input[name="properties[${name}]"]:checked`);
		if (checked) return checked.value;
		const select = scope.querySelector(`select[name="properties[${name}]"]`);
		if (select && select.value) return select.value;
		return fallback;
	}

	// Option text rendered by Liquid carries newlines/indentation and a trailing
	// " (+$xx.xx)" price hint -- collapse whitespace so the summary reads clean.
	function optionLabel(select, fallback) {
		if (select && select.value) {
			const opt = select.options[select.selectedIndex];
			if (opt) return opt.text.replace(/\s+/g, " ").trim();
		}
		return fallback;
	}

	// The Handlebar Width / Stem Length fields only apply once a real handle bar
	// is chosen. When a handle-bar upgrade select exists we hide + disable them
	// while it sits on "No Handle Bar" (value === ""), so they neither validate
	// nor submit an empty cart property; picking a handlebar reveals them.
	function updateHandlebarDependents(root) {
		const hbSelect = root.querySelector(".js-handlebar-upgrade");
		if (!hbSelect) return; // no handle-bar upgrade -> leave width/stem as rendered
		const hasHandlebar = !!hbSelect.value;

		root.querySelectorAll("[data-toggle-by-handlebar]").forEach(wrapper => {
			wrapper.style.display = hasHandlebar ? "" : "none";
			wrapper.querySelectorAll("select, input").forEach(field => {
				field.disabled = !hasHandlebar;
			});
		});
	}

	// --- Build specification bridge ---------------------------------------
	// "Build specification" is a real variant option, so its <select> has to live
	// inside <variant-picker> (VariantPicker only reads selects inside itself).
	// The design puts the visible dropdown in this block instead, so the two are
	// mirrored: choosing here writes the value onto the hidden source select and
	// dispatches a bubbling change, which is what makes the variant and price
	// update. See snippets/bike-variant-picker.liquid.
	function buildSpecSource(root) {
		const productId = root.dataset.productId;
		const scope = root.closest(".m-main-product, .shopify-section") || document;
		return (
			scope.querySelector(`variant-picker[data-product-id="${productId}"] [data-build-spec-source]`) ||
			scope.querySelector("[data-build-spec-source]")
		);
	}

	function applyBuildSpec(root) {
		const proxy = root.querySelector(".js-build-spec");
		const source = proxy && buildSpecSource(root);
		if (!proxy || !source || !proxy.value) return;

		const match = Array.from(source.options).find((o) => o.value === proxy.value);
		if (!match || match.selected) return;
		source.value = match.value;
		source.dispatchEvent(new Event("change", { bubbles: true }));
	}

	// --- Build grid row packing -------------------------------------------
	// The desktop grid is 12 columns and every field carries a base span (3, or
	// 2/4/5 for the narrow/wide ones). A product only renders the fields it has,
	// so the spans in a row often don't add up to 12 and the row ends in dead
	// space -- e.g. three span-3 fields (9) then two span-5 fields (10).
	//
	// This walks the visible fields, packs them into rows exactly the way the
	// grid would, and stretches the items of any short row so the row fills all
	// 12 columns. Rows that already add up to 12 are left exactly as authored, so
	// the hand-tuned layouts (wide Drivetrain / Wheelset next to a narrow Fork)
	// keep their proportions.
	//
	// Runs on load, whenever fields are shown/hidden, and on resize -- the field
	// count is dynamic (Handlebar Width / Stem Length appear only once a handle
	// bar is picked) so this can't be baked into the markup.
	const DESKTOP_GRID = "(min-width: 1024px)";
	const GRID_COLUMNS = 12;

	function baseSpan(field) {
		if (field.classList.contains("bike-select-field--span-2")) return 2;
		if (field.classList.contains("bike-select-field--span-4")) return 4;
		if (field.classList.contains("bike-select-field--span-5")) return 5;
		return 3;
	}

	// Spread `GRID_COLUMNS` over one row's fields: an even share each, with any
	// remainder going to the widest fields first so they stay the widest.
	function stretchRow(row) {
		const share = Math.floor(GRID_COLUMNS / row.length);
		let remainder = GRID_COLUMNS % row.length;
		const spans = new Map(row.map(f => [f, share]));

		row
			.slice()
			.sort((a, b) => baseSpan(b) - baseSpan(a))
			.forEach(field => {
				if (remainder-- > 0) spans.set(field, spans.get(field) + 1);
			});

		row.forEach(field => {
			field.style.gridColumn = "span " + spans.get(field);
		});
	}

	function packBuildGrid(root) {
		const grid = root.querySelector(".bike-customize-grid");
		if (!grid) return;

		const fields = Array.from(grid.querySelectorAll(".bike-select-field"));

		// Below the desktop breakpoint the CSS switches to two equal columns, so
		// hand the layout back to it.
		if (!window.matchMedia(DESKTOP_GRID).matches) {
			fields.forEach(field => { field.style.gridColumn = ""; });
			return;
		}

		const visible = fields.filter(field => field.offsetParent !== null);
		fields.forEach(field => { field.style.gridColumn = ""; });

		let row = [];
		let used = 0;
		const flush = () => {
			if (row.length && used < GRID_COLUMNS) stretchRow(row);
			row = [];
			used = 0;
		};

		visible.forEach(field => {
			const span = baseSpan(field);
			if (used + span > GRID_COLUMNS) flush();
			row.push(field);
			used += span;
		});
		flush();
	}

	// The selected Group Set may restrict which Power Meter Addon options apply.
	// Each group-set <option> carries a `data-power-meters` list of allowed
	// power-meter variant ids (from the `custom.compatible_power_meters`
	// metafield). Empty list = no restriction (show all).
	function filterPowerMeters(root) {
		const gsSelect = root.querySelector(".js-groupset-upgrade");
		const pmSelect = root.querySelector(".js-power-upgrade");
		if (!gsSelect || !pmSelect) return;

		const opt = gsSelect.options[gsSelect.selectedIndex];
		const allow = new Set(
			(opt && opt.dataset.powerMeters ? opt.dataset.powerMeters : "")
				.split(",")
				.map(s => s.trim())
				.filter(Boolean)
		);

		let selectionHidden = false;
		Array.from(pmSelect.options).forEach(o => {
			if (o.value === "") {
				o.hidden = false; // the "No Power Meter" default is always available
				return;
			}
			const show = allow.size === 0 || allow.has(o.value);
			o.hidden = !show;
			if (!show && o.selected) selectionHidden = true;
		});

		// If the currently-selected power meter is no longer compatible, reset to
		// the default so the cart can't receive a mismatched combination.
		if (selectionHidden) pmSelect.value = "";

		// Auto-pick the label mode from the visible subset: a single product ->
		// show variant labels (crank/chainring, e.g. FORCE AXS); several products
		// -> show product names only (e.g. the Magene list). Both forms are
		// pre-rendered on each option as data attributes.
		const visible = Array.from(pmSelect.options).filter(o => o.value && !o.hidden);
		const products = new Set(visible.map(o => o.dataset.productId).filter(Boolean));
		const useVariantLabel = products.size <= 1;
		Array.from(pmSelect.options).forEach(o => {
			if (!o.value) return; // leave the "No Power Meter" default alone
			const label = useVariantLabel ? o.dataset.labelVariant : o.dataset.labelProduct;
			if (label) o.textContent = label;
		});
	}

	// Recomputes the summary line, current-value labels, "customized" state,
	// and upgrade price total for a single root element. Safe to call as
	// often as needed (on every relevant change/input inside that root).
	function updateSummary(root) {
		const bike = root.querySelector(".bike-customization");
		if (!bike) return;

		const summaryFork = bike.querySelector(".summary-fork");
		const summarySeatpost = bike.querySelector(".summary-seatpost");
		const summarySetback = bike.querySelector(".summary-setback");
		const summaryOffset = bike.querySelector(".summary-offset");
		const summaryComment = bike.querySelector(".summary-comment");
		const summaryHighlight = bike.querySelector(".summary-highlight");
		const summaryCommentItem = bike.querySelector(".bike-summary-comment");
		const summaryDrivetrain = bike.querySelector(".summary-drivetrain");
		const summaryPower = root.querySelector(".summary-power");
		const summaryWheel = root.querySelector(".summary-wheel");
		const summaryHandlebar = root.querySelector(".summary-handlebar");
		const summaryGroupset = root.querySelector(".summary-groupset");
		const summaryBottombracket = root.querySelector(".summary-bottombracket");
		const summaryWidth = root.querySelector(".summary-width");
		const summaryStem = root.querySelector(".summary-stem");
		const summaryHandlebarItem = root.querySelector(".bike-summary-handlebar");
		const summaryWidthItem = root.querySelector(".bike-summary-width");
		const summaryStemItem = root.querySelector(".bike-summary-stem");

		const currentFork = bike.querySelector(".current-fork");
		const currentSeatpost = bike.querySelector(".current-seatpost");
		const currentSetback = bike.querySelector(".current-setback");
		const currentOffset = bike.querySelector(".current-offset");
		const currentDrivetrain = bike.querySelector(".current-drivetrain");
		const currentPower = root.querySelector(".current-power");
		const currentWheel = root.querySelector(".current-wheel");
		const currentHandlebar = root.querySelector(".current-handlebar");
		const currentGroupset = root.querySelector(".current-groupset");
		const currentBottombracket = root.querySelector(".current-bottombracket");

		const title = bike.querySelector(".customization-title");
		const comment = bike.querySelector(".product-comment-input");

		// True stock defaults come from data-default attributes rendered by
		// Liquid, not from the live text content (which changes as the user
		// makes selections) -- otherwise "changed from stock" comparisons
		// would drift after the first edit.
		const defaultFork = currentFork?.dataset.default ?? "";
		const defaultSeatpost = currentSeatpost?.dataset.default ?? "";
		const defaultSetback = currentSetback?.dataset.default ?? "";
		const defaultOffset = summaryOffset?.dataset.default ?? currentOffset?.dataset.default ?? "";
		const defaultPower = summaryPower?.dataset.default ?? "";
		const defaultWheel = summaryWheel?.dataset.default ?? "";
		const defaultHandlebar = summaryHandlebar?.dataset.default ?? "";
		const defaultGroupset = summaryGroupset?.dataset.default ?? "";
		const defaultBottombracket = summaryBottombracket?.dataset.default ?? "";
		const defaultDrivetrain =
			summaryDrivetrain?.dataset.default ||
			currentDrivetrain?.dataset.default ||
			"170mm Crank Length 48/35T Chainring 11-34T Cassette";

		const forkValue = getSelectedRadioValue(bike, "Fork", defaultFork);
		const seatpostValue = getSelectedRadioValue(bike, "Seatpost", defaultSeatpost);
		const setbackValue = getSelectedRadioValue(bike, "Setback", defaultSetback);
		const offsetValue = getSelectedRadioValue(bike, "Offset", defaultOffset);
		const commentValue = comment ? comment.value.trim() : "";

		const drivetrainSelect = bike.querySelector(".js-drivetrain-select");
		const drivetrainValue = drivetrainSelect ? drivetrainSelect.value : defaultDrivetrain;

		// NOTE: the upgrade selects live outside the `.bike-customization`
		// element (they're siblings under the shared root), so they must be
		// looked up from `root`, not `bike`.
		const powerSelect = root.querySelector(".js-power-upgrade");
		const wheelSelect = root.querySelector(".js-wheel-upgrade");
		const handlebarSelect = root.querySelector(".js-handlebar-upgrade");
		const groupsetSelect = root.querySelector(".js-groupset-upgrade");
		const bottombracketSelect = root.querySelector(".js-bottombracket-upgrade");

		const powerTitle = optionLabel(powerSelect, defaultPower);
		const wheelTitle = optionLabel(wheelSelect, defaultWheel);
		const handlebarTitle = optionLabel(handlebarSelect, defaultHandlebar);
		const groupsetTitle = optionLabel(groupsetSelect, defaultGroupset);
		const bottombracketTitle = optionLabel(bottombracketSelect, defaultBottombracket);

		// Handlebar Width / Stem Length -- only meaningful while enabled (they get
		// disabled when no handle bar is selected, see updateHandlebarDependents).
		const widthField = root.querySelector(".js-handlebar-width");
		const stemField = root.querySelector(".js-stem-length");
		const widthActive = widthField && !widthField.disabled && !!widthField.value;
		const stemActive = stemField && !stemField.disabled && !!stemField.value;
		const widthValue = widthField ? widthField.value : "";
		const stemValue = stemField ? stemField.value : "";

		function updateField(el, value, changed) {
			if (!el) return;
			el.textContent = value;
			el.classList.toggle("summary-active", changed);
		}

		updateField(summaryFork, forkValue, forkValue !== defaultFork);
		updateField(summarySeatpost, seatpostValue, seatpostValue !== defaultSeatpost);
		updateField(summarySetback, setbackValue, setbackValue !== defaultSetback);
		updateField(summaryOffset, offsetValue, offsetValue !== defaultOffset);
		updateField(summaryDrivetrain, drivetrainValue, drivetrainValue !== defaultDrivetrain);
		updateField(summaryPower, powerTitle, powerTitle !== defaultPower);
		updateField(summaryWheel, wheelTitle, wheelTitle !== defaultWheel);
		updateField(summaryHandlebar, handlebarTitle, handlebarTitle !== defaultHandlebar);
		updateField(summaryGroupset, groupsetTitle, groupsetTitle !== defaultGroupset);
		updateField(summaryBottombracket, bottombracketTitle, bottombracketTitle !== defaultBottombracket);
		updateField(summaryWidth, widthValue, widthActive);
		updateField(summaryStem, stemValue, stemActive);

		if (summaryComment) summaryComment.textContent = commentValue;
		if (summaryHighlight) summaryHighlight.classList.toggle("summary-active", commentValue !== "");
		if (summaryCommentItem) summaryCommentItem.style.display = commentValue ? "inline" : "none";
		if (summaryHandlebarItem) summaryHandlebarItem.style.display = handlebarSelect && handlebarSelect.value ? "inline-block" : "none";
		if (summaryWidthItem) summaryWidthItem.style.display = widthActive ? "inline-block" : "none";
		if (summaryStemItem) summaryStemItem.style.display = stemActive ? "inline-block" : "none";

		if (currentFork) currentFork.textContent = forkValue;
		if (currentSeatpost) currentSeatpost.textContent = seatpostValue;
		if (currentSetback) currentSetback.textContent = setbackValue;
		if (currentOffset) currentOffset.textContent = offsetValue;
		if (currentDrivetrain) currentDrivetrain.textContent = drivetrainValue;
		if (currentPower) currentPower.textContent = powerTitle;
		if (currentWheel) currentWheel.textContent = wheelTitle;
		if (currentHandlebar) currentHandlebar.textContent = handlebarTitle;
		if (currentGroupset) currentGroupset.textContent = groupsetTitle;
		if (currentBottombracket) currentBottombracket.textContent = bottombracketTitle;

		const isCustomized =
			forkValue !== defaultFork ||
			seatpostValue !== defaultSeatpost ||
			setbackValue !== defaultSetback ||
			offsetValue !== defaultOffset ||
			drivetrainValue !== defaultDrivetrain ||
			commentValue !== "" ||
			powerTitle !== defaultPower ||
			wheelTitle !== defaultWheel ||
			handlebarTitle !== defaultHandlebar ||
			groupsetTitle !== defaultGroupset ||
			bottombracketTitle !== defaultBottombracket;

		if (title) {
			title.textContent = isCustomized ? "CUSTOMIZED" : "STOCK SETUP";
			title.classList.toggle("customized", isCustomized);
		}

		// Actually write the chosen upgrade titles into the hidden line-item
		// property inputs so the bike's own cart line reflects the selection.
		const hiddenPower = root.querySelector(".js-selected-power-meter");
		const hiddenWheel = root.querySelector(".js-selected-wheelset");
		const hiddenHandlebar = root.querySelector(".js-selected-handlebar");
		const hiddenGroupset = root.querySelector(".js-selected-groupset");
		const hiddenBottombracket = root.querySelector(".js-selected-bottombracket");
		if (hiddenPower) hiddenPower.value = powerTitle;
		if (hiddenWheel) hiddenWheel.value = wheelTitle;
		if (hiddenHandlebar) hiddenHandlebar.value = handlebarTitle;
		if (hiddenGroupset) hiddenGroupset.value = groupsetTitle;
		if (hiddenBottombracket) hiddenBottombracket.value = bottombracketTitle;

		updateUpgradeTotal(root);
	}

	function updateUpgradeTotal(root) {
		const selects = root.querySelectorAll(".bike-upgrade-select");
		if (!selects.length) return;

		let total = 0;
		selects.forEach(select => {
			const option = select.options[select.selectedIndex];
			total += parseInt(option?.dataset.price || 0, 10);
		});

		const message = root.querySelector(".js-upgrade-total-message");
		const price = root.querySelector(".js-upgrade-total-price");
		if (message && price) {
			if (total > 0) {
				message.style.display = "block";
				price.textContent = formatMoney(total);
			} else {
				message.style.display = "none";
			}
		}

		// Reflect base price + upgrades in the main product price block, if present.
		const priceContainer = document.querySelector('[id^="Price-"][data-base-price]');
		if (!priceContainer) return;

		const basePrice = parseInt(priceContainer.dataset.basePrice || 0, 10);
		if (!basePrice) return;

		const displayPriceEls = priceContainer.querySelectorAll(".m-price-item--regular, .m-price-item--sale");
		if (!displayPriceEls.length) return;

		displayPriceEls.forEach(el => {
			if (el.dataset.originalText === undefined) {
				el.dataset.originalText = el.textContent;
			}
			el.textContent = total > 0 ? formatMoney(basePrice + total) : el.dataset.originalText;
		});
	}

	function clearFieldError(field) {
		if (!field) return;
		field.classList.remove("error");
		const wrapper = field.closest(".m-product-custom-field, .bike-select-field");
		wrapper?.querySelector("bike-select")?.classList.remove("error");
		const error = wrapper?.querySelector(".field-error");
		if (error) {
			error.style.display = "none";
			error.textContent = "";
		}
	}

	function showFieldError(field, message) {
		if (!field) return;
		field.classList.add("error");
		const wrapper = field.closest(".m-product-custom-field, .bike-select-field");
		wrapper?.querySelector("bike-select")?.classList.add("error");
		const error = wrapper?.querySelector(".field-error");
		if (error) {
			error.textContent = message;
			error.style.display = "block";
		}
	}

	// A required field only counts when it's actually applicable -- disabled
	// (hidden) width/stem fields are skipped so they don't block add-to-cart.
	function isActiveRequired(field) {
		return field && !field.disabled;
	}

	function updateRequiredMessage(root) {
		const handlebar = root.querySelector(".js-handlebar-width");
		const stem = root.querySelector(".js-stem-length");
		const buildSpec = root.querySelector(".js-build-spec");
		const requiredMessage = root.querySelector(".js-bike-required-message");
		if (!requiredMessage) return;

		const handlebarOk = !isActiveRequired(handlebar) || handlebar.value;
		const stemOk = !isActiveRequired(stem) || stem.value;
		const buildSpecOk = !isActiveRequired(buildSpec) || buildSpec.value;
		requiredMessage.style.display = handlebarOk && stemOk && buildSpecOk ? "none" : "block";
	}

	function validateRequiredFields(root) {
		const handlebar = root.querySelector(".js-handlebar-width");
		const stem = root.querySelector(".js-stem-length");
		const buildSpec = root.querySelector(".js-build-spec");
		let valid = true;

		clearFieldError(handlebar);
		clearFieldError(stem);
		clearFieldError(buildSpec);

		if (isActiveRequired(buildSpec) && !buildSpec.value) {
			valid = false;
			showFieldError(buildSpec, "Please select a Build specification.");
		}
		if (isActiveRequired(handlebar) && !handlebar.value) {
			valid = false;
			showFieldError(handlebar, "Please select Handlebar Width.");
		}
		if (isActiveRequired(stem) && !stem.value) {
			valid = false;
			showFieldError(stem, "Please select Stem Length.");
		}

		const requiredMessage = root.querySelector(".js-bike-required-message");
		if (requiredMessage) requiredMessage.style.display = valid ? "none" : "block";

		return valid;
	}

	// --- Delegated events -----------------------------------------------
	// Listening on `document` (instead of attaching to specific elements at
	// parse time) means this all keeps working for bike-customization
	// blocks added to the page after this script runs -- e.g. quick view
	// modals or ajax-loaded product cards.

	document.addEventListener("click", evt => {
		const toggle = evt.target.closest(".customization-toggle");
		if (toggle) {
			const bike = toggle.closest(".bike-customization");
			if (bike) {
				bike.classList.toggle("editing");
				toggle.textContent = bike.classList.contains("editing") ? "Done" : "Adjust";
			}
			return;
		}

		const optionalHeader = evt.target.closest(".optional-header");
		if (optionalHeader) {
			optionalHeader.closest(".optional-upgrades")?.classList.toggle("active");
			return;
		}

		const addToCartButton = evt.target.closest(".bike-custom-add-to-cart");
		if (addToCartButton) {
			const form = addToCartButton.closest("form");
			const root = getRoot(addToCartButton);
			if (!form || !root) return;

			if (!validateRequiredFields(root)) return;

			const bundleId = crypto.randomUUID();
			let bundleInput = form.querySelector('input[name="properties[_bundleId]"]');
			if (!bundleInput) {
				bundleInput = document.createElement("input");
				bundleInput.type = "hidden";
				bundleInput.name = "properties[_bundleId]";
				form.appendChild(bundleInput);
			}
			bundleInput.value = bundleId;

			// Read the selections fresh at submit time (rather than trusting
			// whatever a stale "change" event last wrote to a shared global),
			// so a second/third click without an intervening change event
			// still submits the correct upgrades. Any enabled upgrade select
			// with a chosen variant becomes its own cart line item.
			const variants = [];
			root.querySelectorAll(".bike-upgrade-select").forEach(select => {
				if (!select.disabled && select.value) variants.push(select.value);
			});

			window.selectedBikeUpgrades = {
				productId: root.dataset.productId || null,
				bundleId,
				variants
			};

			form.requestSubmit();
			return;
		}
	});

	document.addEventListener("change", evt => {
		const root = getRoot(evt.target);
		if (!root) return;

		if (evt.target.matches(".js-handlebar-upgrade")) {
			updateHandlebarDependents(root);
			packBuildGrid(root);
			updateSummary(root);
			updateRequiredMessage(root);
			return;
		}

		if (evt.target.matches(".js-groupset-upgrade")) {
			filterPowerMeters(root);
			updateSummary(root);
			return;
		}

		if (evt.target.matches(".js-build-spec")) {
			clearFieldError(evt.target);
			applyBuildSpec(root);
			updateRequiredMessage(root);
			updateSummary(root);
			return;
		}

		// Where each build is its own product (see snippets/bike-build-switcher.liquid)
		// the option values are product URLs rather than variant option values, so
		// picking one leaves this product entirely. Guard against the currently
		// selected build so re-picking it doesn't reload the page.
		if (evt.target.matches(".js-build-switch")) {
			const url = evt.target.value;
			if (url && url !== window.location.pathname) window.location.assign(url);
			return;
		}

		if (evt.target.matches(".js-handlebar-width, .js-stem-length")) {
			clearFieldError(evt.target);
			updateRequiredMessage(root);
			updateSummary(root);
			return;
		}

		if (evt.target.matches('input[name^="properties["], .bike-upgrade-select, .js-drivetrain-select')) {
			updateSummary(root);
		}
	});

	document.addEventListener("input", evt => {
		if (evt.target.matches(".product-comment-input")) {
			const root = getRoot(evt.target);
			if (root) updateSummary(root);
		}
	});

	// --- Add optional upgrades as separate cart line items ---------------
	// Runs whenever the bike itself is successfully added to the cart via
	// Minimog's ajax cart.
	document.addEventListener("product-ajax:added", async evt => {
		const upgrades = window.selectedBikeUpgrades;
		const addedProduct = evt.detail?.product;

		if (!upgrades || !addedProduct || !upgrades.bundleId) return;
		if (String(addedProduct.product_id) !== String(upgrades.productId)) return;
		if (!upgrades.variants || !upgrades.variants.length) return;

		try {
			await new Promise(r => setTimeout(r, 250));

			const cart = await fetch("/cart.js").then(r => r.json());

			// Find THIS bike using its bundle id.
			const bikeItem = cart.items.find(
				item =>
				String(item.product_id) === String(upgrades.productId) &&
				item.properties &&
				item.properties._bundleId === upgrades.bundleId
			);

			if (!bikeItem) {
				console.error("Parent bike not found in cart");
				return;
			}

			const items = upgrades.variants.map(id => ({
				id,
				quantity: bikeItem.quantity,
				properties: {
					"_Upgrade For": bikeItem.product_title,
					"_parentKey": bikeItem.key,
					"_bundleId": upgrades.bundleId
				}
			}));

			if (!items.length) return;

			const response = await fetch("/cart/add.js", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"Accept": "application/json"
				},
				body: JSON.stringify({
					items
				})
			});

			const data = await response.json();

			if (data.status) {
				console.error(data.description || data.message);
				return;
			}

			document.dispatchEvent(
				new CustomEvent("cart:refresh", {
					bubbles: true,
					detail: {
						open: true
					}
				})
			);
		} catch (e) {
			console.error(e);
		} finally {
			window.selectedBikeUpgrades = {
				productId: null,
				bundleId: null,
				variants: []
			};
		}
	});

	// Initial paint: run inside the IIFE so `updateSummary` & friends are in
	// scope. Establishes width/stem visibility and power-meter filtering before
	// any user interaction.
	function init() {
		document.querySelectorAll(".bike-customization-block").forEach(root => {
			updateHandlebarDependents(root);
			packBuildGrid(root);
			filterPowerMeters(root);
			applyBuildSpec(root);
			updateSummary(root);
			updateRequiredMessage(root);
		});
	}

	// Row packing depends on the viewport, so redo it when the width changes.
	let repackTimer = null;
	window.addEventListener("resize", () => {
		clearTimeout(repackTimer);
		repackTimer = setTimeout(() => {
			document.querySelectorAll(".bike-customization-block").forEach(packBuildGrid);
		}, 150);
	});

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}
})();
